import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Phone, PhoneCall, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const useOrgScopedHeaders = (token) => {
  const [orgId, setOrgId] = useState(() => {
    try { return localStorage.getItem('current_organization_id') || null; } catch { return null; }
  });
  useEffect(() => {
    if (!token || orgId) return;
    let cancelled = false;
    fetch(`${API_URL}/api/platform/context`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(ctx => { if (!cancelled && ctx?.organizations?.[0]?.id) { const id = ctx.organizations[0].id; setOrgId(id); try { localStorage.setItem('current_organization_id', id); } catch {} } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token, orgId]);
  const headers = useMemo(() => ({ 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(orgId ? { 'X-Organization-Id': orgId } : {}) }), [token, orgId]);
  return { headers, orgId };
};

const VoiceConcierge = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const { headers, orgId } = useOrgScopedHeaders(token);
  const [config, setConfig] = useState(null);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!headers['X-Organization-Id']) { setLoading(false); return; }
    setLoading(true);
    try {
      const [cRes, rRes, sRes] = await Promise.all([
        fetch(`${API_URL}/api/voice/config`, { headers }),
        fetch(`${API_URL}/api/voice/requests`, { headers }),
        fetch(`${API_URL}/api/voice/stats`, { headers }),
      ]);
      setConfig(cRes.ok ? await cRes.json() : null);
      setRequests(rRes.ok ? await rRes.json() : []);
      setStats(sRes.ok ? await sRes.json() : null);
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const webhookUrl = orgId ? `${API_URL}/api/voice/twilio/inbound?orgId=${orgId}` : '';

  if (!orgId && !loading) return <div className="max-w-6xl mx-auto p-8 text-ink-muted" data-testid="voice-no-org">No active organization. Please log in.</div>;

  return (
    <div className="min-h-screen bg-surface-muted py-8" data-testid="voice-page">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-overline text-accent-500 mb-1">VoiceConcierge</div>
          <h1 className="font-display text-3xl text-ink">Voice Ordering & Callbacks</h1>
        </div>

        {loading ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-500" /></div> : (
          <>
            {/* Config status */}
            <div className={`rounded-xl p-5 mb-6 border ${config?.configured ? 'bg-ok/5 border-ok/30' : 'bg-warn/5 border-warn/30'}`} data-testid="voice-config-status">
              <div className="flex items-start gap-3">
                {config?.configured ? <CheckCircle2 className="w-5 h-5 text-ok mt-0.5" /> : <AlertCircle className="w-5 h-5 text-warn mt-0.5" />}
                <div className="flex-1">
                  <div className="font-medium text-ink">{config?.configured ? `Twilio Voice connected — ${config.from_number}` : 'Twilio Voice not configured yet'}</div>
                  <div className="text-sm text-ink-muted mt-1">
                    {config?.configured
                      ? 'Incoming calls to your Twilio number are answered by VoiceConcierge and captured below.'
                      : 'Add your Twilio Account SID, Auth Token and a voice-enabled number under Integrations to activate. Then set the number\u2019s incoming voice webhook to the URL below.'}
                  </div>
                  {webhookUrl && (
                    <div className="mt-3">
                      <div className="text-xs uppercase tracking-wide text-ink-muted mb-1">Twilio inbound webhook (POST)</div>
                      <code className="block text-xs bg-white border border-surface-border rounded-md px-3 py-2 break-all" data-testid="voice-webhook-url">{webhookUrl}</code>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl p-5 shadow-sm border border-surface-border" data-testid="voice-stat-total">
                  <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center"><PhoneCall className="w-5 h-5 text-primary-500" /></div><div><div className="text-2xl font-display text-ink tabular-nums">{stats.total_requests}</div><div className="text-xs text-ink-muted">Captured Calls</div></div></div>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border border-surface-border" data-testid="voice-stat-new">
                  <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center"><Phone className="w-5 h-5 text-accent-500" /></div><div><div className="text-2xl font-display text-ink tabular-nums">{stats.new_requests}</div><div className="text-xs text-ink-muted">New / Unactioned</div></div></div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-surface-border overflow-hidden">
              {requests.length === 0 ? (
                <div className="p-10 text-center text-ink-muted" data-testid="voice-empty">No captured calls yet. They appear here once your Twilio number receives calls.</div>
              ) : (
                <table className="w-full text-sm" data-testid="voice-requests-table">
                  <thead className="bg-surface-sunken text-ink-muted text-xs uppercase">
                    <tr><th className="text-left px-4 py-3">From</th><th className="text-left px-4 py-3">Transcript</th><th className="text-right px-4 py-3">Confidence</th><th className="text-left px-4 py-3">When</th></tr>
                  </thead>
                  <tbody>
                    {requests.map(r => (
                      <tr key={r.id} className="border-t border-surface-border" data-testid={`voice-row-${r.id}`}>
                        <td className="px-4 py-3 text-ink">{r.from_number || '—'}</td>
                        <td className="px-4 py-3 text-ink">{r.transcript}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{r.confidence != null ? `${(r.confidence * 100).toFixed(0)}%` : '—'}</td>
                        <td className="px-4 py-3 text-ink-muted">{new Date(r.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VoiceConcierge;
